/**
 * RippleField — interactive surface ripples: rain drops, wakes, splashes.
 *
 * A damped 2-D wave equation (u_tt = c²∇²u − γ·u_t) leapfrogged on a small
 * CPU grid over the water plane. Disturbances inject smooth bumps; rings
 * propagate, reflect-absorb at the borders, and decay. Height + gradient
 * pack into a half-float DataTexture the water material samples for extra
 * displacement and normal detail — layered ON TOP of the Gerstner field, so
 * ambient waves and interactive ripples compose.
 *
 * CPU on purpose (same rationale as FoamAccumulator): identical on WebGPU
 * and WebGL2, Node-testable, and trivial at the default 128² — the update is
 * ~16k cells of arithmetic per frame.
 *
 * WORLD ORIGIN (2026-09-11, interactive water): a field can FOLLOW the
 * camera — `setOrigin` moves the grid by whole cells and shifts the stored
 * heights the other way, so a ripple stays anchored to the world while the
 * window slides under it (the interaction texture of a continental sea is
 * a few dozen metres around the viewer, never the whole sheet). The
 * `*World` methods address the field in world XZ; the LOCAL methods stay
 * for plane-local owners (WaterSystem) and default to origin (0, 0).
 */

import * as THREE from 'three/webgpu';

export interface RippleFieldConfig {
  /** Water plane size (world units). */
  sizeX: number;
  sizeZ: number;
  /** Grid resolution per side. Default 128 (clamped 32..192). */
  resolution?: number;
  /** Ripple propagation speed in m/s. Default 2.2. */
  waveSpeed?: number;
  /** Velocity damping rate 1/s (higher = ripples die faster). Default 1.2. */
  damping?: number;
}

export class RippleField {
  readonly texture: THREE.DataTexture;
  private readonly texData: Uint16Array;
  private cur: Float32Array;
  private prev: Float32Array;
  private readonly res: number;
  private readonly cellX: number;
  private readonly cellZ: number;
  private readonly cfg: Required<RippleFieldConfig>;
  /** Largest stable step for the leapfrog (CFL 0.6 of cell/height speed). */
  private readonly maxStep: number;
  /** True when any cell is non-zero — lets a still pond skip all work. */
  private active = false;
  /** World position of the grid centre (see setOrigin). */
  private originX = 0;
  private originZ = 0;
  /** Scratch for the whole-cell shift (allocated once). */
  private shiftBuf: Float32Array | null = null;

  constructor(config: RippleFieldConfig) {
    this.cfg = {
      sizeX: config.sizeX,
      sizeZ: config.sizeZ,
      resolution: Math.min(192, Math.max(32, Math.floor(config.resolution ?? 128))),
      waveSpeed: Math.max(0.1, config.waveSpeed ?? 2.2),
      damping: Math.max(0, config.damping ?? 1.2),
    };
    this.res = this.cfg.resolution;
    this.cellX = this.cfg.sizeX / (this.res - 1);
    this.cellZ = this.cfg.sizeZ / (this.res - 1);
    const cell = Math.min(this.cellX, this.cellZ);
    // The CFL step, capped at 1/30 s: a coarse field (half-metre cells at
    // 2.2 m/s allows 0.136 s) would otherwise step less often than the
    // frame and — worse — never at all, because the hitch clamp below held
    // the time debt under one step (the 2026-09-11 frozen-field bug: a
    // 64 m / 128² interaction window stamped rings that never rang).
    this.maxStep = Math.min((0.6 * cell) / this.cfg.waveSpeed, 1 / 30);

    this.cur = new Float32Array(this.res * this.res);
    this.prev = new Float32Array(this.res * this.res);
    this.texData = new Uint16Array(this.res * this.res * 4);
    this.texture = new THREE.DataTexture(this.texData, this.res, this.res, THREE.RGBAFormat, THREE.HalfFloatType);
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.colorSpace = THREE.NoColorSpace;
    this.texture.needsUpdate = true;
  }

  /** World-space centre of the field (the owner feeds it to the shader). */
  get origin(): { x: number; z: number } {
    return { x: this.originX, z: this.originZ };
  }

  /** Grid spacing (world units per cell, X axis). */
  get cellSize(): number {
    return this.cellX;
  }

  /** Whether any ripple is alive (a still field costs nothing). */
  get isActive(): boolean {
    return this.active;
  }

  /**
   * Move the field's world origin to (x, z), snapped to WHOLE cells so no
   * resampling blurs the rings: the heights shift by the opposite integer
   * offset, cells that slide out of the window are dropped, cells that
   * slide in start still. Returns true when the grid moved.
   */
  setOrigin(x: number, z: number): boolean {
    const dc = Math.round((x - this.originX) / this.cellX);
    const dr = Math.round((z - this.originZ) / this.cellZ);
    if (dc === 0 && dr === 0) return false;
    this.originX += dc * this.cellX;
    this.originZ += dr * this.cellZ;
    if (!this.active) return true;
    const n = this.res;
    if (Math.abs(dc) >= n || Math.abs(dr) >= n) {
      this.cur.fill(0);
      this.prev.fill(0);
      this.active = false;
      this.pack();
      return true;
    }
    const buf = (this.shiftBuf ??= new Float32Array(n * n));
    for (const field of [this.cur, this.prev]) {
      buf.fill(0);
      // new[row][col] = old[row + dr][col + dc]: the world point that was at
      // cell (col + dc) is at cell col after the origin moved by +dc cells.
      const r0 = Math.max(0, -dr);
      const r1 = Math.min(n, n - dr);
      const c0 = Math.max(0, -dc);
      const c1 = Math.min(n, n - dc);
      for (let row = r0; row < r1; row++) {
        const src = (row + dr) * n + c0 + dc;
        buf.set(field.subarray(src, src + (c1 - c0)), row * n + c0);
      }
      field.set(buf);
    }
    this.pack();
    return true;
  }

  /** `disturb` addressed in WORLD XZ (see setOrigin). */
  disturbWorld(worldX: number, worldZ: number, strength: number, radius = 0.35): void {
    this.disturb(worldX - this.originX, worldZ - this.originZ, strength, radius);
  }

  /** Ripple height at a WORLD position (0 outside the window). */
  heightAtWorld(worldX: number, worldZ: number): number {
    const lx = worldX - this.originX;
    const lz = worldZ - this.originZ;
    if (Math.abs(lx) > this.cfg.sizeX * 0.5 || Math.abs(lz) > this.cfg.sizeZ * 0.5) return 0;
    return this.heightAtLocal(lx, lz);
  }

  /** Ripple slope (dh/dx, dh/dz) at a WORLD position — a hull rocks on it. */
  slopeAtWorld(worldX: number, worldZ: number): { dx: number; dz: number } {
    if (!this.active) return { dx: 0, dz: 0 };
    const lx = worldX - this.originX;
    const lz = worldZ - this.originZ;
    if (Math.abs(lx) > this.cfg.sizeX * 0.5 || Math.abs(lz) > this.cfg.sizeZ * 0.5) return { dx: 0, dz: 0 };
    const n = this.res;
    const col = Math.min(n - 2, Math.max(1, Math.round((lx / this.cfg.sizeX + 0.5) * (n - 1))));
    const row = Math.min(n - 2, Math.max(1, Math.round((lz / this.cfg.sizeZ + 0.5) * (n - 1))));
    const cur = this.cur;
    return {
      dx: (cur[row * n + col + 1] - cur[row * n + col - 1]) / (2 * this.cellX),
      dz: (cur[(row + 1) * n + col] - cur[(row - 1) * n + col]) / (2 * this.cellZ),
    };
  }

  /**
   * Inject a disturbance at LOCAL plane coordinates (origin at the plane
   * center): a smooth gaussian bump of `strength` world units (negative =
   * depression, e.g. a raindrop) with the given radius in world units.
   */
  disturb(localX: number, localZ: number, strength: number, radius = 0.35): void {
    const n = this.res;
    const cx = (localX / this.cfg.sizeX + 0.5) * (n - 1);
    const cz = (localZ / this.cfg.sizeZ + 0.5) * (n - 1);
    const rCellsX = Math.max(1, radius / this.cellX);
    const rCellsZ = Math.max(1, radius / this.cellZ);
    const x0 = Math.max(0, Math.floor(cx - rCellsX * 2));
    const x1 = Math.min(n - 1, Math.ceil(cx + rCellsX * 2));
    const z0 = Math.max(0, Math.floor(cz - rCellsZ * 2));
    const z1 = Math.min(n - 1, Math.ceil(cz + rCellsZ * 2));
    for (let row = z0; row <= z1; row++) {
      for (let col = x0; col <= x1; col++) {
        const dx = (col - cx) / rCellsX;
        const dz = (row - cz) / rCellsZ;
        const falloff = Math.exp(-(dx * dx + dz * dz));
        if (falloff < 0.02) continue;
        // Write BOTH buffers: a pure displacement with zero initial velocity.
        // (cur-only would give the bump an implicit Verlet velocity of
        // bump/dt — a huge energy kick instead of a drop.)
        this.cur[row * n + col] += strength * falloff;
        this.prev[row * n + col] += strength * falloff;
      }
    }
    this.active = true;
  }

  /** Ripple height at LOCAL plane coordinates (adds onto the Gerstner height). */
  heightAtLocal(localX: number, localZ: number): number {
    if (!this.active) return 0;
    const n = this.res;
    const col = Math.min(n - 1, Math.max(0, Math.round((localX / this.cfg.sizeX + 0.5) * (n - 1))));
    const row = Math.min(n - 1, Math.max(0, Math.round((localZ / this.cfg.sizeZ + 0.5) * (n - 1))));
    return this.cur[row * n + col];
  }

  /** Unstepped time carried between frames (leapfrog needs FIXED steps). */
  private timeDebt = 0;

  /** Advance the wave equation (internally substepped for stability). */
  update(dt: number): void {
    if (!this.active || dt <= 0) return;
    // Leapfrog encodes velocity as (u − prev)/h — every step must use the
    // SAME h or small remainder steps get re-read as large-step velocities
    // (an exponential energy pump, found by the damping unit test). So run
    // fixed-size steps and carry the remainder into the next frame.
    this.timeDebt = Math.min(this.timeDebt + dt, Math.max(0.1, this.maxStep * 2)); // hitch clamp (always ≥ one step)
    const h = this.maxStep;
    let stepped = false;
    while (this.timeDebt >= h) {
      this.step(h);
      this.timeDebt -= h;
      stepped = true;
    }
    if (stepped) this.pack();
  }

  private step(dt: number): void {
    const n = this.res;
    const cur = this.cur;
    const prev = this.prev;
    const c2dt2 = this.cfg.waveSpeed * this.cfg.waveSpeed * dt * dt;
    const kx = c2dt2 / (this.cellX * this.cellX);
    const kz = c2dt2 / (this.cellZ * this.cellZ);
    const velKeep = Math.max(0, 1 - this.cfg.damping * dt);

    let maxAbs = 0;
    for (let row = 0; row < n; row++) {
      const rN = Math.max(0, row - 1) * n;
      const rS = Math.min(n - 1, row + 1) * n;
      const r = row * n;
      for (let col = 0; col < n; col++) {
        const i = r + col;
        const west = cur[r + Math.max(0, col - 1)];
        const east = cur[r + Math.min(n - 1, col + 1)];
        const north = cur[rN + col];
        const south = cur[rS + col];
        const u = cur[i];
        const lap = kx * (west + east - 2 * u) + kz * (north + south - 2 * u);
        let next = u + (u - prev[i]) * velKeep + lap;
        // Absorbing rim: the outermost cells bleed energy instead of
        // reflecting rings back across the pond.
        if (row === 0 || col === 0 || row === n - 1 || col === n - 1) next *= 0.7;
        prev[i] = next; // prev becomes the NEXT field; buffers swap below
        const a = Math.abs(next);
        if (a > maxAbs) maxAbs = a;
      }
    }
    const t = this.cur;
    this.cur = this.prev;
    this.prev = t;
    if (maxAbs < 5e-5) {
      this.cur.fill(0);
      this.prev.fill(0);
      this.active = false;
    }
  }

  /** Pack height + gradient into the half-float texture (r=h, g=dh/dx, b=dh/dz). */
  private pack(): void {
    const n = this.res;
    const cur = this.cur;
    const toHalf = THREE.DataUtils.toHalfFloat;
    for (let row = 0; row < n; row++) {
      const r = row * n;
      const rN = Math.max(0, row - 1) * n;
      const rS = Math.min(n - 1, row + 1) * n;
      for (let col = 0; col < n; col++) {
        const i = r + col;
        const dhdx = (cur[r + Math.min(n - 1, col + 1)] - cur[r + Math.max(0, col - 1)]) / (2 * this.cellX);
        const dhdz = (cur[rS + col] - cur[rN + col]) / (2 * this.cellZ);
        const o = i * 4;
        this.texData[o] = toHalf(cur[i]);
        this.texData[o + 1] = toHalf(dhdx);
        this.texData[o + 2] = toHalf(dhdz);
        this.texData[o + 3] = toHalf(1);
      }
    }
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}
